import { useEffect, useState } from "react";
import { Calculator, Landmark } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { fmtIdr } from "@/lib/format";

const EXPOSURES = [
  { key: "fx_usd_revenue_pct", label: "Revenue priced in USD", hint: "Share of room / F&B / other revenue quoted in USD (OTA, wholesale, expat contracts)", testid: "assumption-fx-revenue-input" },
  { key: "fx_usd_cogs_pct", label: "Cost of sales in USD", hint: "Imported food, beverage & supplies", testid: "assumption-fx-cogs-input" },
  { key: "fx_usd_expense_pct", label: "Operating expenses in USD", hint: "OTA commissions, software, franchise / management fees", testid: "assumption-fx-expense-input" },
];

export const FxAssumptions = ({ a, set, budgetYear }) => {
  const [live, setLive] = useState(null);
  const [avg, setAvg] = useState(null);
  const num = (v) => (v === "" || v === "-" ? v : Number(v));
  const group = live?.budget_rates?.[String(budgetYear)];
  useEffect(() => {
    api.get("/fx/latest").then((r) => setLive(r.data)).catch(() => setLive(null));
    api.get("/fx/average", { params: { to: "IDR" } }).then((r) => setAvg(r.data)).catch(() => setAvg(null));
  }, []);
  const base = Number(a.fx_base_rate) || 0;
  const bud = Number(a.fx_budget_rate) || 0;
  const change = base > 0 && bud > 0 ? (bud / base - 1) * 100 : 0;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6" data-testid="fx-assumptions-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><Landmark size={16} className="text-emerald-700" /> Foreign Exchange (USD/IDR)</h3>
          <p className="text-xs text-slate-500 mt-1">Rate change = budget rate ÷ base rate − 1, applied to the USD-exposed share of each line.</p>
        </div>
        <div className={`text-right rounded px-3 py-1.5 border ${Math.abs(change) > 0.005 ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="fx-change-badge">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">FX change</p>
          <p className={`font-mono text-sm font-semibold ${change > 0 ? "text-amber-700" : change < 0 ? "text-emerald-700" : "text-slate-500"}`} data-testid="fx-change-value">{change > 0 ? "+" : ""}{change.toFixed(2)}%</p>
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5" data-testid="assumption-field-fx-base-rate">
          <Label className="text-xs font-medium tracking-wide text-slate-600">Base rate — actual year average</Label>
          <div className="relative">
            <Input type="number" step="1" value={a.fx_base_rate ?? ""} placeholder={avg ? String(Math.round(avg.average)) : "e.g. 16250"} onChange={(e) => set("fx_base_rate", e.target.value === "" ? null : num(e.target.value))} className="font-mono pr-12" data-testid="assumption-fx-base-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">IDR</span>
          </div>
          <p className="text-[11px] text-slate-500">{a.fx_base_rate ? fmtIdr(a.fx_base_rate) : "Not set"} per USD
            {avg && <> · <button type="button" className="text-emerald-700 font-semibold hover:underline inline-flex items-center gap-1" data-testid="fx-use-12m-average" onClick={() => set("fx_base_rate", Math.round(avg.average))}><Calculator size={10} /> Use 12-month average {fmtIdr(avg.average)}</button></>}
          </p>
        </div>
        <div className="space-y-1.5" data-testid="assumption-field-fx-budget-rate">
          <Label className="text-xs font-medium tracking-wide text-slate-600">Budget rate {budgetYear}</Label>
          <div className="relative">
            <Input type="number" step="1" value={a.fx_budget_rate ?? ""} placeholder={group ? String(group.rate) : live?.rates?.IDR ? String(live.rates.IDR) : "e.g. 16500"} onChange={(e) => set("fx_budget_rate", e.target.value === "" ? null : num(e.target.value))} className="font-mono pr-12" data-testid="assumption-fx-budget-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">IDR</span>
          </div>
          <p className="text-[11px] text-slate-500">{a.fx_budget_rate ? fmtIdr(a.fx_budget_rate) : "Not set"} per USD
            {group && <> · <button type="button" className="text-emerald-700 font-semibold hover:underline" data-testid="fx-use-group-rate" onClick={() => set("fx_budget_rate", group.rate)}>Use group budget rate {fmtIdr(group.rate)}</button></>}
            {!group && live?.rates?.IDR && <> · <button type="button" className="text-emerald-700 font-semibold hover:underline" data-testid="fx-use-live-rate" onClick={() => set("fx_budget_rate", live.rates.IDR)}>Use live rate {fmtIdr(live.rates.IDR)} ({live.rate_date})</button></>}
          </p>
        </div>
        {EXPOSURES.map((f) => (
          <div key={f.key} className="space-y-1.5" data-testid={`assumption-field-${f.key.replace(/_/g, "-")}`}>
            <Label className="text-xs font-medium tracking-wide text-slate-600">{f.label}</Label>
            <div className="relative">
              <Input type="number" step="1" min="0" max="100" value={a[f.key] ?? 0} onChange={(e) => set(f.key, num(e.target.value))} className="font-mono pr-10" data-testid={f.testid} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
            </div>
            <p className="text-[11px] text-slate-500">{f.hint}{Math.abs(change) > 0.005 && Number(a[f.key]) > 0 ? ` · effect ${(change * Number(a[f.key]) / 100) > 0 ? "+" : ""}${(change * Number(a[f.key]) / 100).toFixed(2)}%` : ""}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
